import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { money } from "../lib/format";
import { ui } from "../lib/ui";

interface Producto {
  id: number;
  sku: string;
  barcode?: string | null;
  nombre: string;
  categoria?: string | null;
  unidad: string;
  precio_costo: number;
  precio_venta: number;
  iva_pct: number;
  stock: number;
  stock_minimo: number;
  activo: boolean;
}

const PAGE_SIZE = 50;

const emptyForm: Partial<Producto> = {
  sku: "",
  barcode: "",
  nombre: "",
  categoria: "",
  unidad: "UN",
  precio_costo: 0,
  precio_venta: 0,
  iva_pct: 21,
  stock: 0,
  stock_minimo: 0,
  activo: true,
};

export default function Productos() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [categoria, setCategoria] = useState<string>("");
  const [tipoUnidad, setTipoUnidad] = useState<"todos" | "unidad" | "pesable">(
    "todos"
  );
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<Producto | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(0), [debounced, categoria, tipoUnidad]);

  const PESABLE_UNITS = new Set(["KG", "GR", "LT", "ML"]);
  const isPesable = (u: string | undefined) =>
    !!u && PESABLE_UNITS.has(u.toUpperCase());

  const filtros = { q: debounced, categoria, offset: page * PAGE_SIZE };

  const { data, isLoading, error } = useQuery({
    queryKey: ["productos", filtros],
    queryFn: async () => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: filtros.offset,
      };
      if (filtros.q) params.q = filtros.q;
      if (filtros.categoria) params.categoria = filtros.categoria;
      const { data } = await api.get<Producto[]>("/api/products", { params });
      return data;
    },
  });

  const categorias = useMemo(() => {
    const set = new Set<string>();
    data?.forEach((p) => p.categoria && set.add(p.categoria));
    return Array.from(set).sort();
  }, [data]);

  return (
    <div className="flex-1 flex flex-col h-full">
      <header className={ui.header}>
        <div>
          <h1 className={ui.title}>Productos</h1>
          <p className={ui.subtitle}>Catálogo del kiosko</p>
        </div>
        <button className={ui.btnPrimary} onClick={() => setCreating(true)}>
          + Nuevo producto
        </button>
      </header>

      <div className={ui.page}>
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            placeholder="Buscar por SKU, código o nombre..."
            className={ui.input + " max-w-md"}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className={ui.input + " max-w-xs"}
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
          >
            <option value="">Todas las categorías</option>
            {categorias.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <select
            className={ui.input + " max-w-[180px]"}
            value={tipoUnidad}
            onChange={(e) =>
              setTipoUnidad(e.target.value as typeof tipoUnidad)
            }
            title="Filtrar por tipo de unidad"
          >
            <option value="todos">Unid + Kg/Lt</option>
            <option value="unidad">Solo por unidad</option>
            <option value="pesable">Solo pesables (KG/GR/LT/ML)</option>
          </select>
        </div>

        {error && <div className={ui.error}>Error al cargar productos.</div>}

        <div className={ui.card}>
          <table className={ui.table}>
            <thead className={ui.thead}>
              <tr>
                <th className={ui.th}>SKU</th>
                <th className={ui.th}>Nombre</th>
                <th className={ui.th}>Categoría</th>
                <th className={ui.th + " text-center"}>Unidad</th>
                <th className={ui.th + " text-right"}>Stock</th>
                <th className={ui.th + " text-right"}>Precio</th>
                <th className={ui.th + " text-center"}>Activo</th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td colSpan={6} className="p-3">
                      <div className={ui.skeleton + " h-6 w-full"} />
                    </td>
                  </tr>
                ))}
              {!isLoading &&
                data
                  ?.filter((p) => {
                    if (tipoUnidad === "todos") return true;
                    const pesable = isPesable(p.unidad);
                    return tipoUnidad === "pesable" ? pesable : !pesable;
                  })
                  .map((p) => {
                    const pesable = isPesable(p.unidad);
                    return (
                      <tr
                        key={p.id}
                        className={ui.tr}
                        onClick={() => setEditing(p)}
                      >
                        <td className={ui.td + " font-mono text-xs"}>
                          {p.sku}
                        </td>
                        <td className={ui.td + " font-medium text-slate-900"}>
                          {p.nombre}
                        </td>
                        <td className={ui.td}>{p.categoria || "—"}</td>
                        <td className={ui.td + " text-center"}>
                          <span
                            className={
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase " +
                              (pesable
                                ? "bg-amber-100 text-amber-700"
                                : "bg-slate-100 text-slate-600")
                            }
                          >
                            {pesable && <span>⚖</span>}
                            {p.unidad || "UN"}
                          </span>
                        </td>
                        <td
                          className={
                            ui.td +
                            " text-right font-semibold tabular-nums " +
                            (Number(p.stock) < Number(p.stock_minimo)
                              ? "text-rose-600"
                              : "text-slate-700")
                          }
                        >
                          {pesable
                            ? Number(p.stock).toFixed(3)
                            : Number(p.stock)}
                        </td>
                        <td
                          className={
                            ui.td +
                            " text-right font-bold text-slate-900 tabular-nums"
                          }
                        >
                          {money(p.precio_venta)}
                          {pesable && (
                            <span className="text-[10px] text-slate-400 font-normal">
                              {" "}
                              /{(p.unidad || "kg").toLowerCase()}
                            </span>
                          )}
                        </td>
                        <td className={ui.td + " text-center"}>
                          <span
                            className={
                              ui.badge +
                              " " +
                              (p.activo
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-slate-100 text-slate-500")
                            }
                          >
                            {p.activo ? "Sí" : "No"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              {!isLoading && !data?.length && (
                <tr>
                  <td colSpan={7} className={ui.empty}>
                    Sin productos. Creá el primero con "+ Nuevo producto".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
          <span>
            Página {page + 1} — {data?.length ?? 0} resultados
          </span>
          <div className="flex gap-2">
            <button
              className={ui.btnSecondary + " py-1.5 px-3"}
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Anterior
            </button>
            <button
              className={ui.btnSecondary + " py-1.5 px-3"}
              disabled={(data?.length ?? 0) < PAGE_SIZE}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>

      {(creating || editing) && (
        <ProductoModal
          producto={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["productos"] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function ProductoModal({
  producto,
  onClose,
  onSaved,
}: {
  producto: Producto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<Partial<Producto>>(() =>
    producto ? { ...producto } : { ...emptyForm }
  );
  const isEdit = !!producto;

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const { sku: _sku, id: _id, ...rest } = form as Producto;
        await api.patch(`/api/products/${producto!.id}`, rest);
      } else {
        await api.post("/api/products", form);
      }
    },
    onSuccess: onSaved,
  });

  const update = (k: keyof Producto, v: unknown) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className={ui.modalOverlay} onClick={onClose}>
      <div className={ui.modalCardLg} onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-slate-900 mb-4">
          {isEdit ? "Editar producto" : "Nuevo producto"}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-1">
            <label className={ui.label}>SKU</label>
            <input
              className={ui.input}
              value={form.sku ?? ""}
              disabled={isEdit}
              onChange={(e) => update("sku", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Código de barras</label>
            <input
              className={ui.input}
              value={form.barcode ?? ""}
              onChange={(e) => update("barcode", e.target.value)}
            />
          </div>
          <div className="col-span-2">
            <label className={ui.label}>Nombre</label>
            <input
              className={ui.input}
              value={form.nombre ?? ""}
              onChange={(e) => update("nombre", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Categoría</label>
            <input
              className={ui.input}
              value={form.categoria ?? ""}
              onChange={(e) => update("categoria", e.target.value)}
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Unidad</label>
            <select
              className={ui.input}
              value={form.unidad ?? "UN"}
              onChange={(e) => update("unidad", e.target.value)}
            >
              <option value="UN">UN (unidad)</option>
              <option value="KG">KG (kilogramo, pesable)</option>
              <option value="GR">GR (gramo, pesable)</option>
              <option value="LT">LT (litro, pesable)</option>
              <option value="ML">ML (mililitro, pesable)</option>
            </select>
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Precio costo</label>
            <input
              type="number"
              step="0.01"
              className={ui.input}
              value={Number(form.precio_costo ?? 0)}
              onChange={(e) =>
                update("precio_costo", parseFloat(e.target.value) || 0)
              }
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Precio venta</label>
            <input
              type="number"
              step="0.01"
              className={ui.input}
              value={Number(form.precio_venta ?? 0)}
              onChange={(e) =>
                update("precio_venta", parseFloat(e.target.value) || 0)
              }
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>IVA %</label>
            <input
              type="number"
              step="0.01"
              className={ui.input}
              value={Number(form.iva_pct ?? 21)}
              onChange={(e) =>
                update("iva_pct", parseFloat(e.target.value) || 0)
              }
            />
          </div>
          <div className="col-span-1">
            <label className={ui.label}>Stock mínimo</label>
            <input
              type="number"
              step="0.01"
              className={ui.input}
              value={Number(form.stock_minimo ?? 0)}
              onChange={(e) =>
                update("stock_minimo", parseFloat(e.target.value) || 0)
              }
            />
          </div>
          {!isEdit && (
            <div className="col-span-1">
              <label className={ui.label}>Stock inicial</label>
              <input
                type="number"
                step="0.01"
                className={ui.input}
                value={Number(form.stock ?? 0)}
                onChange={(e) =>
                  update("stock", parseFloat(e.target.value) || 0)
                }
              />
            </div>
          )}
          <div className="col-span-2 flex items-center gap-2 pt-2">
            <input
              id="activo"
              type="checkbox"
              checked={!!form.activo}
              onChange={(e) => update("activo", e.target.checked)}
              className="w-4 h-4 accent-emerald-500"
            />
            <label htmlFor="activo" className="text-sm text-slate-700">
              Producto activo
            </label>
          </div>
        </div>

        {save.isError && (
          <div className={ui.error + " mt-4"}>
            Error guardando producto.
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button className={ui.btnSecondary} onClick={onClose}>
            Cancelar
          </button>
          <button
            className={ui.btnPrimary}
            disabled={save.isPending || !form.sku || !form.nombre}
            onClick={() => save.mutate()}
          >
            {save.isPending ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
